import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  Box,
  Button,
  Center,
  Flex,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Text,
  VStack,
  useColorMode,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { AddIcon, SearchIcon } from '@chakra-ui/icons';
import { LuLayers, LuChevronDown, LuChevronRight } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import ConfirmationDialog from '../shared/ConfirmationDialog';
import { deleteTag, fetchCategories, fetchTags, updateTag } from '../../api/endpointsBackend';
import { useUrlSyncedId } from '../../hooks/useUrlSyncedId';
import { useTagsView } from '../../hooks/useTagsView';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import DashboardCurrencyTabs from '../dashboard/DashboardCurrencyTabs';
import DisplayCurrencySelector from '../shared/DisplayCurrencySelector';
import PageHeader from '../shared/PageHeader';
import { categoryTint } from '../../utils/categoryTint';
import TagDetailPanel from './TagDetailPanel';
import useIsPhone from '../../hooks/useIsPhone';
import PhoneTags from '../phone/PhoneTags';

import { useResizablePanel } from '../../hooks/useResizablePanel';
import PanelResizeHandle, { pagePanelOptions, resizablePanelWidth } from '../shared/PanelResizeHandle';
const PANEL_CREATE_MODE = 'new';
const FALLBACK_TAG_COLOR = '#6B7B8D';

// One source of truth for the amount-column width: the header cell and the row
// cell both read it, which is what keeps a label over its own numbers.
const AMOUNT_COL_W = { base: '90px', md: '130px' };
const NAME_COL_W = 240;
const AMOUNT_COL_PX = 130;
const COL_GAP_PX = 16;
// Width the row needs before all three amount columns can be shown: the name
// column, three amount cells and the gaps between them. The cells are
// flexShrink={0} so that right edges stay aligned, which means that below this
// width they do not compress — they overflow leftward across the tag name.
const MIN_W_FOR_DEBT = NAME_COL_W + 3 * AMOUNT_COL_PX + 2 * COL_GAP_PX;

function HeaderCell({ label }) {
  return (
    <Text
      textStyle="microLabel"
      w={AMOUNT_COL_W}
      textAlign="right"
      flexShrink={0}
    >
      {label}
    </Text>
  );
}

function AmountCell({ value, currency, muted = false }) {
  // A zero reads as noise in a column that is blank for most tags, so an
  // absent figure shows an em dash instead of "0" (MI-316).
  if (!value) {
    return (
      <Flex w={AMOUNT_COL_W} justify="flex-end" flexShrink={0}>
        <Text fontSize="2xs" color="redesign.text.faint">—</Text>
      </Flex>
    );
  }
  return (
    <Flex w={AMOUNT_COL_W} justify="flex-end" align="baseline" gap={1} flexShrink={0}>
      <Text
        fontSize="sm"
        fontWeight="600"
        color={muted ? 'redesign.text.muted' : 'redesign.heading'}
      >
        <Text as="span" fontSize="2xs" mr={1} color="redesign.text.muted">{currency}</Text>
        {value.toLocaleString('en-US', { maximumFractionDigits: 0 })}
      </Text>
    </Flex>
  );
}

function CurrencyLine({ row, currency, showDebtColumns }) {
  const c = row.byCurrency[currency];
  if (!c) return null;

  return (
    <Flex align="center" py="2px" justify="flex-end" gap={{ base: 2, md: 4 }}>
      <AmountCell value={c.spent} currency={currency} />
      {showDebtColumns && (
        <>
          <AmountCell value={c.iOwe} currency={currency} muted />
          <AmountCell value={c.owedToMe} currency={currency} muted />
        </>
      )}
    </Flex>
  );
}

function TagRow({ row, isSelected, isExpanded, onClick, onToggleExpand, showDebtColumns }) {
  const { t } = useTranslation('tags');
  const color = row.color || FALLBACK_TAG_COLOR;
  const { colorMode } = useColorMode();
  const currencies = Object.keys(row.byCurrency).sort();
  const isMulti = currencies.length > 1;
  const visibleCurrencies = (isMulti && !isExpanded) ? [currencies[0]] : currencies;

  return (
    <Flex
      align="flex-start"
      w="100%"
      px={1}
      py="4px"
      borderRadius="lg"
      bg={isSelected ? 'redesign.bg.accent' : 'transparent'}
      _hover={{ bg: isSelected ? 'redesign.bg.accent' : 'redesign.bg.hover' }}
      cursor="pointer"
      onClick={() => onClick(row)}
      opacity={row.is_active ? 1 : 0.55}
    >
      {/* Left: chevron + dot + name */}
      <Flex
        align="center"
        gap={{ base: 1, md: 2 }}
        flex={{ base: 1, md: '0 0 auto' }}
        w={{ md: '240px' }}
        minW={0}
        h="22px"
      >
        {isMulti ? (
          <Box
            as="button"
            onClick={(e) => { e.stopPropagation(); onToggleExpand(row.id); }}
            flexShrink={0}
            display="flex"
            alignItems="center"
            justifyContent="center"
            w="20px"
            h="20px"
            borderRadius="md"
            // The tag's own hue, in a shade that reads on the row.
            color={categoryTint(color, colorMode).fg}
            _hover={{ bg: 'redesign.bg.hover' }}
            aria-label={isExpanded ? t('row.collapseAria') : t('row.expandAria')}
          >
            <Box as={isExpanded ? LuChevronDown : LuChevronRight} boxSize="16px" strokeWidth={3} />
          </Box>
        ) : (
          <Flex w="20px" h="20px" align="center" justify="center" flexShrink={0}>
            <Box w="6px" h="6px" borderRadius="full" bg={color} />
          </Flex>
        )}
        <Text fontSize="sm" color="redesign.heading" isTruncated>
          {row.name}
        </Text>
        {!row.is_active && (
          <Text textStyle="microLabel" flexShrink={0}>
            {t('row.inactive')}
          </Text>
        )}
      </Flex>

      {/* Middle: stacked currency lines */}
      <Box flex={1} minW={0}>
        {visibleCurrencies.map((c) => (
          <CurrencyLine key={c} row={row} currency={c} showDebtColumns={showDebtColumns} />
        ))}
      </Box>

    </Flex>
  );
}

// The phone gets its own read-only page; the desktop body stays as it was.
export default function TagsPage(props) {
  return useIsPhone() ? <PhoneTags {...props} /> : <TagsPageDesktop {...props} />;
}

function TagsPageDesktop() {
  const { t } = useTranslation('tags');
  const { can } = useWorkspace();
  const canCreate = can('tag.create');
  const canUpdate = can('tag.update');
  const canDelete = can('tag.delete');
  const [selectedTagId, setSelectedTagId] = useUrlSyncedId('tag_id');
  const [searchTerm, setSearchTerm] = useState('');
  const [showHidden, setShowHidden] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState('all');
  const [expandedTagIds, setExpandedTagIds] = useState(() => new Set());
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [tagToDelete, setTagToDelete] = useState(null);

  const handleToggleExpand = (tagId) => {
    setExpandedTagIds((prev) => {
      const next = new Set(prev);
      if (next.has(tagId)) next.delete(tagId);
      else next.add(tagId);
      return next;
    });
  };
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const toast = useToast();

  const { loading, error, rows, tabCurrencies, refetch } = useTagsView({ selectedCurrency });

  const [allCategories, setAllCategories] = useState([]);
  const [allTags, setAllTags] = useState([]);
  useEffect(() => {
    fetchCategories().then(setAllCategories).catch(() => {});
    fetchTags(null).then(setAllTags).catch(() => {});
  }, []);

  const searchedRows = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) => r.name?.toLowerCase().includes(q) || r.description?.toLowerCase().includes(q)
    );
  }, [rows, searchTerm]);

  const hiddenCount = useMemo(
    () =>
      searchedRows.filter(
        (r) => r.is_active === false || Object.keys(r.byCurrency).length === 0
      ).length,
    [searchedRows]
  );

  const displayRows = useMemo(() => {
    const list = showHidden
      ? searchedRows
      : searchedRows.filter(
          (r) => r.is_active !== false && Object.keys(r.byCurrency).length > 0
        );
    return [...list].sort((a, b) => {
      const aEmpty = Object.keys(a.byCurrency).length === 0;
      const bEmpty = Object.keys(b.byCurrency).length === 0;
      if (aEmpty !== bEmpty) return aEmpty ? 1 : -1;
      if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
      return b.grandSpent - a.grandSpent;
    });
  }, [searchedRows, showHidden]);

  const isPanelOpen = selectedTagId !== null;
  // Same drag-to-size panel as the Movements page.
  const panelResize = useResizablePanel(isPanelOpen, pagePanelOptions(440));
  // Debt columns stay hidden until some tag has a debt leg, so a workspace
  // that only tracks plain spending keeps the narrow two-column table.
  const hasAnyDebt = useMemo(() => rows.some((r) => r.hasDebt), [rows]);

  // Measure the list itself, not the viewport. This list sits beside a detail
  // pane that is always on screen, so on a 1192px display the rows only get
  // ~490px — a viewport breakpoint reads "desktop, plenty of room" and shows
  // three columns that cannot fit, and they overflow across the tag name.
  const listRef = useRef(null);
  const [listWidth, setListWidth] = useState(0);
  useEffect(() => {
    const el = listRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([entry]) => {
      setListWidth(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Below the threshold only Gastado shows; the debt figures stay available in
  // the detail panel.
  const showDebtColumns = hasAnyDebt && listWidth >= MIN_W_FOR_DEBT;

  const selectedRow = rows.find((r) => r.id === selectedTagId);

  const handleCreateTag = () => setSelectedTagId(PANEL_CREATE_MODE);
  const handleEditTag = (tag) => setSelectedTagId(tag.id);
  const handleClosePanel = () => setSelectedTagId(null);
  const handleTagUpdated = () => refetch();

  const handleToggleStatus = async (tag) => {
    try {
      await updateTag(tag.id, { is_active: !tag.is_active });
      await refetch();
      toast({
        title: t('toasts.successTitle'),
        description: tag.is_active
          ? t('toasts.deactivated', { name: tag.name })
          : t('toasts.activated', { name: tag.name }),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: t('toasts.errorTitle'),
        description: t('toasts.statusFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDeleteTag = async (tag) => {
    try {
      await deleteTag(tag.id, true);
      setTagToDelete(tag);
      onDeleteOpen();
    } catch (err) {
      let errorMessage = t('toasts.deleteFailed');
      if (err.response?.status === 409) {
        const detail = err.response.data?.detail || '';
        const m = detail.match(/(\d+) movement/);
        errorMessage = m
          ? t('toasts.associatedCount', { count: parseInt(m[1], 10) })
          : t('toasts.associatedSome');
      } else if (err.response?.status === 404) {
        errorMessage = t('toasts.notFound');
      }
      toast({ title: t('toasts.errorTitle'), description: errorMessage, status: 'error', duration: 7000, isClosable: true });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!tagToDelete) return;
    try {
      setDeleteLoading(true);
      await deleteTag(tagToDelete.id);
      toast({ title: t('toasts.successTitle'), description: t('toasts.deleted'), status: 'success', duration: 3000, isClosable: true });
      onDeleteClose();
      if (selectedTagId === tagToDelete.id) setSelectedTagId(null);
      setTagToDelete(null);
      await refetch();
    } catch (err) {
      const errorMessage = err.response?.data?.detail || t('toasts.deleteFailed');
      toast({ title: t('toasts.errorTitle'), description: errorMessage, status: 'error', duration: 7000, isClosable: true });
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Flex ref={panelResize.containerRef} h={{ base: 'calc(100vh - 56px)', md: 'var(--mk-page-h)' }} overflow="hidden" position="relative">
      <Box flex={1} display="flex" flexDirection="column" minW={0}>
        <PageHeader title={t('title')}>
          <Flex align="center" gap={2}>
            {/* MI-298: global "display in" control — the ONLY selector on this
                page; the detail panel just reacts to it. */}
            <DisplayCurrencySelector />
            {tabCurrencies && tabCurrencies.length > 1 && (
              <DashboardCurrencyTabs
                currencies={tabCurrencies}
                selected={selectedCurrency}
                onChange={setSelectedCurrency}
              />
            )}
            {canCreate && (
              <IconButton
                icon={<AddIcon />}
                onClick={handleCreateTag}
                aria-label={t('newAria')}
                size="sm"
                variant="ghost"
                color="redesign.text.muted"
                _hover={{ color: 'redesign.heading', bg: 'redesign.bg.card' }}
              />
            )}
          </Flex>
        </PageHeader>

        <Box ref={listRef} flex={1} overflowY="auto" overflowX="hidden" px={{ base: 3, md: 5 }} py={5}>
          <Flex justify="flex-end" align="center" mb={4} gap={3}>
            <InputGroup size="sm" maxW="250px">
              <InputLeftElement pointerEvents="none" h="32px">
                <SearchIcon color="redesign.text.muted" boxSize={3} />
              </InputLeftElement>
              <Input
                placeholder={t('searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                borderRadius="md"
                fontSize="sm"
                h="32px"
              />
            </InputGroup>
          </Flex>

          <Flex align="center" mb={2} px={1}>
            <Box w={{ md: '240px' }} flex={{ base: 1, md: '0 0 auto' }} minW={0} />
            {/* Mirrors TagRow's amount block exactly — same wrapper, same cell
                widths, same gap — so each label sits over its own column.
                flex={1} + minW={0} matches the row's middle Box, which is what
                keeps the two right edges on the same pixel. */}
            <Flex flex={1} minW={0} align="center" justify="flex-end" gap={{ base: 2, md: 4 }}>
              <HeaderCell label={t('columns.spent')} />
              {showDebtColumns && (
                <>
                  <HeaderCell label={t('columns.iOwe')} />
                  <HeaderCell label={t('columns.owedToMe')} />
                </>
              )}
            </Flex>
          </Flex>

          {loading ? (
            <Center py={20}>
              <VStack spacing={4}>
                <Spinner size="xl" color="redesign.text.muted" />
                <Text color="redesign.text">{t('loading')}</Text>
              </VStack>
            </Center>
          ) : error ? (
            <Center py={20}><Text color="redesign.accent.red">{error}</Text></Center>
          ) : searchedRows.length === 0 ? (
            <Center py={20}>
              <VStack spacing={4}>
                <Text fontSize="xl" color="redesign.text.muted">
                  {searchTerm ? t('empty.withSearch') : t('empty.noTags')}
                </Text>
                {!searchTerm && canCreate && (
                  <Button leftIcon={<AddIcon />} colorScheme="blue" onClick={handleCreateTag}>
                    {t('empty.createButton')}
                  </Button>
                )}
              </VStack>
            </Center>
          ) : (
            <>
              <VStack spacing={0} align="stretch">
                {displayRows.map((row) => (
                  <TagRow
                    key={row.id}
                    row={row}
                    isSelected={selectedTagId === row.id}
                    isExpanded={expandedTagIds.has(row.id)}
                    onClick={(r) => handleEditTag(r)}
                    onToggleExpand={handleToggleExpand}
                    showDebtColumns={showDebtColumns}
                  />
                ))}
              </VStack>
              {hiddenCount > 0 && (
                <Box
                  as="button"
                  type="button"
                  w="100%"
                  mt={1}
                  py={2}
                  px={2}
                  textAlign="center"
                  fontSize="xs"
                  color="redesign.text.muted"
                  _hover={{ color: 'redesign.heading' }}
                  onClick={() => setShowHidden((v) => !v)}
                >
                  {showHidden ? t('hideHidden', { count: hiddenCount }) : t('showHidden', { count: hiddenCount })}
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      <Box
        display={{ base: isPanelOpen ? 'block' : 'none', md: 'block' }}
        // `fixed` at base, not `absolute`: an absolute panel scrolls away with
        // the page, taking its header — and the only close button — off screen,
        // which strands the user inside the panel on a short viewport.
        position={{ base: 'fixed', md: 'relative' }}
        right={{ base: 0, md: 'auto' }}
        top={{ base: 0, md: 'auto' }}
        bottom={{ base: 0, md: 'auto' }}
        left={{ base: 0, md: 'auto' }}
        w={{ base: '100%', md: resizablePanelWidth('44%', 440), lg: resizablePanelWidth('42%', 440) }}
        h={{ base: '100dvh', md: '100%' }}
        bg="redesign.bg.surface"
        borderLeft="1px solid"
        borderLeftColor="redesign.border"
        boxShadow={{ base: '-2px 0 8px rgba(0,0,0,0.15)', md: 'none' }}
        // Above the mobile top bar (Sidebar.js — fixed, 44px, zIndex 100),
        // which otherwise covers the panel header and its close button.
        zIndex={{ base: 200, md: 'auto' }}
      >
        <PanelResizeHandle resize={panelResize} />
        {isPanelOpen ? (
          <TagDetailPanel
            tagId={selectedTagId === PANEL_CREATE_MODE ? null : selectedTagId}
            selectedRow={selectedTagId === PANEL_CREATE_MODE ? null : selectedRow}
            categories={allCategories}
            allTags={allTags}
            selectedCurrency={selectedCurrency}
            onClose={handleClosePanel}
            onTagUpdated={canUpdate ? handleTagUpdated : undefined}
            onDelete={canDelete ? handleDeleteTag : undefined}
            onToggleStatus={canUpdate ? handleToggleStatus : undefined}
          />
        ) : (
          <Flex direction="column" align="center" justify="center" gap={3} h="100%" bg="redesign.bg.page" color="redesign.text.faint">
            <Box as={LuLayers} boxSize="32px" />
            <Text fontSize="sm" color="redesign.text.muted">{t('selectToView')}</Text>
          </Flex>
        )}
      </Box>

      <ConfirmationDialog
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        onConfirm={handleDeleteConfirm}
        title={t('deleteDialog.title')}
        message={tagToDelete ? t('deleteDialog.message', { name: tagToDelete.name }) : ''}
        confirmText={t('deleteDialog.confirm')}
        confirmColorScheme="red"
        isLoading={deleteLoading}
      />
    </Flex>
  );
}
